// Single sign-on into the Sales Reporter (backend/sales_app_bridge.py → /sso),
// through the Sales Reporter's own existing Logbook hand-off (sso_login.php).
//
// The Sales Reporter runs on the ERP's own origin (/api/sales-app/app/) and
// keeps its sign-in in localStorage ("sr_token" + "sr_user"). When an ERP
// user opens it, we ask the ERP server for a Sales Reporter session for THAT
// user (the ERP server gets it server-to-server with a short-lived signed
// link, never through the browser) and store it where the Sales Reporter
// looks, so it opens straight into the app — no second login.
//
// "sr_sso_for" records which ERP user the stored session belongs to, when it
// was issued and how (via: "sso" | "manual"), so a different person signing in
// on the same browser never inherits someone else's Sales Reporter session,
// an automatic one is renewed before it lapses, and one the person made with
// the Sales Reporter's own form is left alone.
import api, { API_BASE } from "@/lib/api";

const TOKEN_KEY = "sr_token";
const USER_KEY = "sr_user";
const OWNER_KEY = "sr_sso_for";
const DIR_SYNC_KEY = "sr_dir_sync_at";
const RENEW_AFTER_MS = 11 * 3600 * 1000;   // renew well before a session could lapse
const DIR_SYNC_EVERY_MS = 3600 * 1000;
const DIR_RETRY_MS = 10 * 60 * 1000;       // after a refused read (not a GM/CEO)

const ls = {
  get(k) { try { return localStorage.getItem(k); } catch { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch { /* storage blocked */ } },
  del(k) { try { localStorage.removeItem(k); } catch { /* storage blocked */ } },
};

function owner() {
  try { return JSON.parse(ls.get(OWNER_KEY) || "null"); } catch { return null; }
}

// Ends the stored Sales Reporter session on its server (best effort) and
// forgets it locally. Never goes through the shared axios client: its 401
// handler would sign the person out of the ERP.
async function revoke(token) {
  if (!token) return;
  let erpToken = "";
  try { erpToken = localStorage.getItem("token") || ""; } catch { /* storage blocked */ }
  try {
    await fetch(`${API_BASE}/sales-app/app/api/logout.php`, {
      method: "POST",
      credentials: "include",
      headers: { "X-Session-Token": token, ...(erpToken ? { Authorization: `Bearer ${erpToken}` } : {}) },
    });
  } catch { /* offline — the session still expires on its own */ }
}

function forget() {
  ls.del(TOKEN_KEY);
  ls.del(USER_KEY);
  ls.del(OWNER_KEY);
}

// The ERP matches people to Sales Reporter accounts by email through a member
// directory only a Sales Reporter GM/CEO can read (the Sales Reporter decides
// that). Whenever there is a session here, try to refresh it with THAT
// session, server-side — at most hourly, or every 10 min after a refusal.
export async function syncSalesReporterDirectory({ force = false } = {}) {
  const token = ls.get(TOKEN_KEY);
  if (!token) return { ok: false, error: "Not signed in to the Sales Reporter in this browser." };
  let last = {};
  try { last = JSON.parse(ls.get(DIR_SYNC_KEY) || "{}") || {}; } catch { last = {}; }
  const wait = last.ok ? DIR_SYNC_EVERY_MS : DIR_RETRY_MS;
  if (!force && last.token === token && Date.now() - (last.at || 0) < wait) return { ok: !!last.ok, skipped: true };
  try {
    const { data } = await api.post("/sales-app/directory/sync", null, { headers: { "X-Session-Token": token } });
    ls.set(DIR_SYNC_KEY, JSON.stringify({ at: Date.now(), ok: true, token }));
    return { ok: true, members: data?.members };
  } catch (e) {
    ls.set(DIR_SYNC_KEY, JSON.stringify({ at: Date.now(), ok: false, token }));
    const d = e?.response?.data?.detail;
    return { ok: false, error: typeof d === "string" ? d : "Couldn't read the Sales Reporter's member list." };
  }
}

// The Sales Reporter page saved a session itself (someone used its own login
// form): record whose it is, so it is never reused for another ERP user.
export function adoptSalesReporterSession(erpUser) {
  if (!erpUser?.id || !ls.get(TOKEN_KEY)) return;
  const mine = owner();
  if (!mine || mine.uid !== erpUser.id || mine.via !== "sso") {
    ls.set(OWNER_KEY, JSON.stringify({ uid: erpUser.id, at: Date.now(), via: "manual" }));
  }
}

export async function endSalesReporterSession() {
  const token = ls.get(TOKEN_KEY);
  forget();
  await revoke(token);
}

/**
 * Make sure the Sales Reporter will open signed in as `erpUser`.
 * Resolves to one of:
 *   { status: "ready" }                  signed in (reused or new session)
 *   { status: "manual" }                 single sign-on isn't set up — the
 *                                         Sales Reporter's own login shows
 *   { status: "denied", message }        no access / no matching account —
 *                                         its own login shows, with message
 */
export async function ensureSalesReporterSession(erpUser, { force = false } = {}) {
  const uid = erpUser?.id;
  if (!uid) return { status: "manual" };
  const mine = owner();
  const token = ls.get(TOKEN_KEY);

  if (mine && mine.uid !== uid) {           // someone else's session on this browser
    forget();
    await revoke(token);
  } else if (!force && token && mine?.uid === uid && (mine.via === "manual" || Date.now() - (mine.at || 0) < RENEW_AFTER_MS)) {
    syncSalesReporterDirectory();
    return { status: "ready" };
  }

  try {
    const { data } = await api.post("/sales-app/sso");
    const previous = ls.get(TOKEN_KEY);
    ls.set(TOKEN_KEY, data.token);
    ls.set(USER_KEY, JSON.stringify(data.user || {}));
    ls.set(OWNER_KEY, JSON.stringify({ uid, at: Date.now(), via: "sso" }));
    if (previous && previous !== data.token) revoke(previous);
    syncSalesReporterDirectory();
    return { status: "ready" };
  } catch (e) {
    const code = e?.response?.status;
    const message = e?.response?.data?.detail;
    if (code === 409) {
      // Not set up, or the member directory hasn't been read yet. A session
      // already in this browser (theirs — someone else's was dropped above)
      // is kept, and used to read the member list (works for a GM/CEO); if
      // that fills the list, try the automatic sign-in once more.
      if (ls.get(TOKEN_KEY)) {
        adoptSalesReporterSession(erpUser);
        const synced = await syncSalesReporterDirectory({ force: true });
        if (synced.ok && !force) return ensureSalesReporterSession(erpUser, { force: true });
        return { status: "ready" };
      }
      return { status: "manual", message: typeof message === "string" ? `Automatic sign-in isn't ready yet: ${message}` : "" };
    }
    // An automatic session that can't be renewed is dropped so the Sales
    // Reporter asks for its own login; one made by hand is kept.
    if (mine?.uid === uid && mine.via !== "manual") forget();
    return { status: "denied", message: typeof message === "string" ? message : "Couldn't sign you in to the Sales Reporter automatically." };
  }
}
